import { Fragment, lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AdminShell } from '@/components/AdminShell';
import { AuthPending } from '@/components/AuthPending';
import { LazyPage } from '@/components/LazyPage';
import { useAuth } from '@/lib/auth';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetSentPage from '@/pages/ResetSentPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import AppsPage from '@/pages/AppsPage';
/*
 * The one page loaded on demand. It carries Lucide's ~1,800 icons for the
 * feature picker — measured: 607 kB of admin became 1,549 kB with them in the
 * main chunk (177 → 344 kB gzipped), paid by every sign-in, dashboard visit
 * and blog edit that never opens an app. Split here, at the route, rather than
 * inside the picker: the editor needs the list synchronously to read a draft
 * (`knownIcon`), and a page is a natural seam where a component is not.
 * `LazyPage` is what stands in while it loads, and where it lands if it never
 * does.
 */
const AppEditorPage = lazy(() => import('@/pages/AppEditorPage'));

const LazyAppEditor = () => <LazyPage page={AppEditorPage} />;
import PostEditorPage from '@/pages/PostEditorPage';
import BlogPage from '@/pages/BlogPage';
import TicketsPage from '@/pages/TicketsPage';
import TicketPage from '@/pages/TicketPage';
import SettingsPage from '@/pages/SettingsPage';
import SecurityPage from '@/pages/SecurityPage';
import ProfilePage from '@/pages/ProfilePage';
import LegalPage from '@/pages/LegalPage';
import SitePage from '@/pages/SitePage';
import ConnectionsPage from '@/pages/ConnectionsPage';
import OAuthConsentPage from '@/pages/OAuthConsentPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  /*
   * The third state matters. The session lives in an httpOnly cookie, so on a
   * page load the answer is not known until `me` comes back — and treating
   * "not yet known" as "not signed in" bounces every refresh to the login
   * screen and back, which reads as being logged out at random.
   */
  if (status === 'loading') return <AuthPending />;
  if (status === 'authenticated') return <>{children}</>;
  // With the page asked for, query included, so signing in lands back on it.
  return (
    <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  );
}

/**
 * A subtree that starts over when the `:id` in the URL changes.
 *
 * Both editors sit on a route with an id in it, so React reuses the same
 * component instance when one is opened from another — same route, same
 * position, new params. Every piece of local state survives that: the draft
 * somebody was typing, and the flags that say what they have touched.
 *
 * Em Linh drove it: with a link from one post editor to another, an unsaved
 * byline picked on the first post is still in state on the second, its "the
 * user chose this" flag still raised, and the next save signs the second post
 * with it. There is no such link today — which is the only reason the editors
 * have got away with it — and it is one link away from being real, on the
 * screens where losing an author is exactly what happened once already.
 *
 * A `key` rather than an effect that resets each field: an effect is a list
 * somebody has to remember to add to, and the thing being forgotten is
 * precisely how this class of bug starts.
 */
function FreshPerId({ children }: { children: React.ReactNode }) {
  const { id } = useParams();
  return <Fragment key={id}>{children}</Fragment>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/forgot-password/sent" element={<ResetSentPage />} />
      {/* Outside RequireAuth: whoever is following a reset link is, by
          definition, the person who cannot sign in. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Signed in, but not in the shell: the consent page is a decision on
          its own, reached from another app, and the sidebar would only be a
          way to wander off it. */}
      <Route
        path="/oauth/consent"
        element={
          <RequireAuth>
            <OAuthConsentPage />
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AdminShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/apps" element={<AppsPage />} />
        <Route path="/apps/:id" element={<FreshPerId><LazyAppEditor /></FreshPerId>} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/tickets" element={<TicketsPage />} />
        {/*
          `FreshPerId` like the editors: moving between two reports must not
          leave the reply box holding what was typed for the last one.
        */}
        <Route path="/tickets/:id" element={<FreshPerId><TicketPage /></FreshPerId>} />
        <Route path="/blog/:id" element={<FreshPerId><PostEditorPage /></FreshPerId>} />
        {/*
          One page per group, not one page with five cards. `useEditorBar` is
          one Save button per screen, and a single "Unsaved changes" covering
          both a password change and the terms of service says nothing useful
          about either. Addresses that can be sent to somebody, too:
          `/settings/legal` is a link, "open Settings and scroll" is not.
        */}
        <Route path="/settings" element={<SettingsPage />}>
          <Route index element={<Navigate to="/settings/security" replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="connections" element={<ConnectionsPage />} />
          <Route path="site" element={<SitePage />} />
          <Route path="legal" element={<LegalPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
